import React from 'react';
import { Trash2, Plus, FileText } from 'lucide-react';
import { BookingDiscount } from '../../lib/discounts';
import { Payment } from '../../lib/payments';

interface BookingPaymentSummaryProps {
  subtotal: number;
  discounts?: BookingDiscount[];
  taxAmount: number;
  totalAmount: number;
  payments?: Payment[];
  onApplyDiscount: () => void;
  onDeleteDiscount: (discountId: string) => Promise<void>;
  onRecordPayment?: () => void;
  onViewInvoice?: () => void;
}

export default function BookingPaymentSummary({
  subtotal,
  discounts,
  taxAmount,
  totalAmount,
  payments = [],
  onApplyDiscount,
  onDeleteDiscount,
  onRecordPayment,
  onViewInvoice
}: BookingPaymentSummaryProps) {
  // No longer using permissions - always render payment summary

  // Calculate total paid amount from completed payments
  const totalPaid = payments
    .filter(p => p.status === 'completed')
    .reduce((sum, p) => sum + p.amount, 0);

  // Calculate subtotal after discounts
  const totalDiscounts = discounts?.reduce((sum, discount) => {
    return sum + (discount.type === 'percentage' 
      ? (subtotal * discount.amount / 100)
      : discount.amount);
  }, 0) || 0;
  
  const subtotalAfterDiscounts = subtotal - totalDiscounts;
  
  // Calculate correct tax amount (5% of subtotal after discounts)
  const correctTaxAmount = Math.round((subtotalAfterDiscounts * 0.05) * 100) / 100;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-900">Payment Summary</h3>
        <button 
          type="button"
          onClick={onApplyDiscount}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Apply Discount
        </button>
      </div>
      <div className="p-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Subtotal</span>
            <span>${(Math.round((subtotal || 0) * 100) / 100).toLocaleString()}</span>
          </div>
          {discounts?.map((discount) => (
            <div key={discount.id} className="flex justify-between text-sm text-gray-600">
              <span className="text-red-600">
                {discount.title} ({discount.type === 'percentage' ? `${discount.amount}%` : '$'})
              </span>
              <div className="flex items-center space-x-2 text-red-600">
                <span>-${(Math.round((discount.type === 'percentage' 
                  ? (subtotal * discount.amount / 100)
                  : discount.amount) * 100) / 100).toLocaleString()}</span>
                <button 
                  onClick={() => onDeleteDiscount(discount.id)}
                  className="text-red-400 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
          <div className="flex justify-between text-sm text-gray-600">
            <span>GST (5%)</span>
            <span>${correctTaxAmount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm text-green-600">
            <span>Amount Paid</span>
            <span>${(Math.round(totalPaid * 100) / 100).toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm font-medium text-gray-900 pt-2 border-t border-gray-200">
            <span>Balance Due</span>
            <span>${(Math.round((totalAmount - totalPaid) * 100) / 100).toLocaleString()}</span>
          </div>
          {(onRecordPayment || onViewInvoice) && (
            <div className="pt-3 space-y-2">
              {onRecordPayment && (
                <button
                  type="button"
                  onClick={onRecordPayment}
                  className="w-full inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-md hover:bg-green-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Record Payment
                </button>
              )}
              {onViewInvoice && (
                <button
                  type="button"
                  onClick={onViewInvoice}
                  className="w-full inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  View Invoice
                </button>
              )}
              {onViewInvoice && <div className="md:hidden h-8"></div>}
              <div className="md:hidden pb-8"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}